import API_CONFIG from '../config/api';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useIsAuthenticated } from '@azure/msal-react';
import { useDemoMode } from '../hooks/useDemoMode';
import LayerStylePanel from './LayerStylePanel';
import LabelOptionsPanel from './LabelOptionsPanel';
import LayerInfoPanel from './LayerInfoPanel';
import LayerSymbologyIcon from './LayerSymbologyIcon';
import MapSharingDialog from './MapSharingDialog';
import './DemoStyles.css';
import './PhotoCollection.css';

const ActiveLayersPanel = ({ map, activeLayers, onRemoveLayer, onClearAllLayers, onReorderLayers, isLeftPanelOpen, hideShareControls = false }) => {
  const { makeAuthenticatedRequest } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const { isDemoMode } = useDemoMode();
  const [layerStates, setLayerStates] = useState(new Map()); // Track visibility state
  const [loading, setLoading] = useState(false);
  const [currentMapStyle, setCurrentMapStyle] = useState('mapbox://styles/mapbox/streets-v11');
  const [showStylePanel, setShowStylePanel] = useState(false);
  const [selectedLayerForStyling, setSelectedLayerForStyling] = useState(null);
  const [showLabelOptionsPanel, setShowLabelOptionsPanel] = useState(false);
  const [selectedLayerForLabels, setSelectedLayerForLabels] = useState(null);
  const [showLayerInfoPanel, setShowLayerInfoPanel] = useState(false);
  const [selectedLayerForInfo, setSelectedLayerForInfo] = useState(null);
  const [layerGroups, setLayerGroups] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null); // { targetId, targetType, position }
  const [draggedGroup, setDraggedGroup] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [dropLineStyle, setDropLineStyle] = useState({ display: 'none' });
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, layer: null });
  const [showSharingDialog, setShowSharingDialog] = useState(false);

  const listRef = useRef(null);
  const contextMenuRef = useRef(null);
  const prevMapRef = useRef(null);

  useEffect(() => {
    // Simplified drop line - always hidden for now
    setDropLineStyle({ display: 'none' });
  }, [dragOverPosition]);

  // If map instance changes (e.g., remounted), reset layer state so layers get re-added
  useEffect(() => {
    if (prevMapRef.current !== map) {
      prevMapRef.current = map;
      if (map) {
        setLayerStates(new Map());
      }
    }
  }, [map]);

  useEffect(() => {
    if (map && activeLayers.length > 0) {
      activeLayers.forEach(layer => {
        if (!layerStates.has(layer.id)) {
          const id = `layer-${layer.id}`;
          const sourceId = `source-${layer.id}`;
          const exists = !!(map.getLayer(id) || map.getLayer(id + '-fill') || map.getLayer(id + '-stroke') || map.getSource(sourceId));
          if (exists) {
            // Layer already on map; mark as loaded without re-adding (preserves custom styling)
            setLayerStates(prev => new Map(prev.set(layer.id, { visible: true, loaded: true })));
          } else {
            addLayerToMap(layer);
          }
        }
      });
    }
  }, [map, activeLayers]);

  useEffect(() => {
    if (map) {
      // Wait for map to be fully loaded before detecting style
      const detectStyle = () => {
        const style = map.getStyle();
        if (style) {
          // Try to get from metadata first
          if (style.metadata && style.metadata['mapbox:origin']) {
            setCurrentMapStyle(style.metadata['mapbox:origin']);
          } else if (style.metadata && style.metadata['mapbox:type']) {
            // Try alternative metadata field
            const styleType = style.metadata['mapbox:type'];
            const matchingStyle = mapStyles.find(s => s.id.includes(styleType));
            if (matchingStyle) {
              setCurrentMapStyle(matchingStyle.id);
            }
          } else {
            // Keep the initial streets-v11 if we can't detect
            // Don't override with unknown
          }
        }
      };

      if (map.loaded()) {
        detectStyle();
      } else {
        map.once('load', detectStyle);
      }
    }
  }, [map]);

  const addLayerToMap = async (dataset) => {
    if (!map || (!isAuthenticated && !isDemoMode)) {
      return;
    }

    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${dataset.id}/geojson`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const layerData = await response.json();

      const layerId = `layer-${dataset.id}`;
      const sourceId = `source-${dataset.id}`;

      // Remove existing layer/source if any
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map.getLayer(layerId + '-labels')) {
        map.removeLayer(layerId + '-labels');
      }
      if (map.getLayer(layerId + '-fill')) {
        map.removeLayer(layerId + '-fill');
      }
      if (map.getLayer(layerId + '-stroke')) {
        map.removeLayer(layerId + '-stroke');
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
      
      // Clean up existing markers for this specific layer (if replacing)
      const isPhotoLayer = dataset.geometry_type?.toLowerCase() === 'photocollection' || 
                          dataset.name?.toLowerCase().includes('photo') ||
                          dataset.file_name?.toLowerCase().includes('photo');
      if (isPhotoLayer) {
        // Initialize photo markers storage if needed
        if (!window.photoMarkersByLayer) {
          window.photoMarkersByLayer = new Map();
        }
        
        // Clean up existing markers for this layer
        const existingMarkers = window.photoMarkersByLayer.get(layerId);
        if (existingMarkers) {
          existingMarkers.forEach(marker => marker.remove());
          window.photoMarkersByLayer.delete(layerId);
        }
      }

      // Handle raster data differently
      if (layerData.type === 'raster' && layerData.rasterData) {
        await addRasterLayerToMap(dataset, layerData.rasterData, layerId, sourceId);
      } else {
        // Handle vector data as before
        await addVectorLayerToMap(dataset, layerData, layerId, sourceId);
      }

      // Update layer state - include raster data for proper symbology
      const layerStateData = { visible: true, loaded: true };
      if (dataset.geometry_type === 'Raster' && layerData.rasterData) {
        layerStateData.raster_data = layerData.rasterData;
      }
      setLayerStates(prev => new Map(prev.set(dataset.id, layerStateData)));

    } catch (err) {
      console.error('❌ Error loading layer:', err);
      console.error('Error details:', err.message);
      console.error('Error stack:', err.stack);
    } finally {
      setLoading(false);
    }
  };

  const addRasterLayerToMap = async (dataset, rasterData, layerId, sourceId) => {
    try {
      // Validate raster data
      if (!rasterData || !rasterData.bbox || !rasterData.preview) {
        throw new Error('Invalid raster data: missing bbox or preview');
      }
      
      // Convert base64 preview to blob URL
      const imageData = `data:image/png;base64,${rasterData.preview}`;
      
      // Create image source for the raster
      map.addSource(sourceId, {
        type: 'image',
        url: imageData,
        coordinates: [
          [rasterData.bbox[0], rasterData.bbox[3]], // top-left
          [rasterData.bbox[2], rasterData.bbox[3]], // top-right
          [rasterData.bbox[2], rasterData.bbox[1]], // bottom-right
          [rasterData.bbox[0], rasterData.bbox[1]]  // bottom-left
        ]
      });

      // Add raster layer
      map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-opacity': 1.0
        }
      });

      // Fit map to raster bounds if it's the first active layer
      if (activeLayers.length === 1) {
        const bbox = rasterData.bbox;
        const bounds = new window.mapboxgl.LngLatBounds([
          [bbox[0], bbox[1]], // southwest
          [bbox[2], bbox[3]]  // northeast
        ]);
        
        if (!bounds.isEmpty()) {
          const defaultPadding = 50;
          const leftPanelWidth = isLeftPanelOpen ? 398 : 48; // Left panel OVERLAPS map
          // Right panel is separate, doesn't overlap, so no extra padding needed
          
          const padding = {
            top: defaultPadding,
            bottom: defaultPadding,
            left: leftPanelWidth + defaultPadding, // Account for overlapping left panel
            right: defaultPadding // Right panel doesn't overlap
          };
          
          map.fitBounds(bounds, { padding });
        }
      }
    } catch (error) {
      console.error('Error adding raster layer:', error);
      throw error;
    }
  };

  const addVectorLayerToMap = async (dataset, geoJsonData, layerId, sourceId) => {
    try {
      // Determine layer style based on geometry type - be more specific about photo detection
      // Use whichever property is available (preferring geometry_type from metadata)
      const geometryType = dataset.geometry_type || dataset.geometryType;
      const isPhotoLayer = geometryType?.toLowerCase() === 'photocollection';
      const layerStyles = getLayerStyle(geometryType, layerId, sourceId, isPhotoLayer);
      
      // Only add source for non-photo layers (photo collections use custom HTML markers)
      if (!isPhotoLayer) {
        // Add source
        map.addSource(sourceId, {
          type: 'geojson',
          data: geoJsonData
        });
      }
      
      // Only add layers if layerStyles is not null (photo collections return null)
      if (layerStyles) {
        if (Array.isArray(layerStyles)) {
          layerStyles.forEach(style => map.addLayer(style));
        } else {
          map.addLayer(layerStyles);
        }
      } else if (isPhotoLayer) {
        // Photo collections: create custom HTML markers instead of layers
        geoJsonData.features.forEach((feature, index) => {
          const coordinates = feature.geometry.coordinates;
          const properties = feature.properties;
          
          // Create thumbnail element
          const thumbnailEl = document.createElement('div');
          thumbnailEl.className = 'photo-marker';
          thumbnailEl.style.cssText = `
            width: 50px;
            height: 50px;
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            background-size: cover;
            background-position: center;
            cursor: pointer;
          `;
          
          // Set background image from thumbnail URL
          if (properties.thumbnail_url) {
            thumbnailEl.style.backgroundImage = `url('${API_CONFIG.BASE_URL.replace('/api', '')}${properties.thumbnail_url}')`;
          } else {
            // Fallback to a camera icon
            thumbnailEl.style.backgroundColor = '#ff6b6b';
            thumbnailEl.innerHTML = '📷';
            thumbnailEl.style.display = 'flex';
            thumbnailEl.style.alignItems = 'center';
            thumbnailEl.style.justifyContent = 'center';
            thumbnailEl.style.fontSize = '20px';
          }
          
          // No hover effects - keep markers in exact locations
          
          // Add click handler to open image in new tab
          thumbnailEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (properties.sharepoint_url) {
              // Open SharePoint image in new tab
              window.open(properties.sharepoint_url, '_blank');
            } else {
              console.warn('No SharePoint URL available for photo:', properties.filename);
            }
          });
          
          // Create and add marker
          const marker = new window.mapboxgl.Marker(thumbnailEl)
            .setLngLat(coordinates)
            .addTo(map);
            
          // Store marker reference for cleanup per layer
          if (!window.photoMarkersByLayer) {
            window.photoMarkersByLayer = new Map();
          }
          if (!window.photoMarkersByLayer.has(layerId)) {
            window.photoMarkersByLayer.set(layerId, []);
          }
          window.photoMarkersByLayer.get(layerId).push(marker);
        });
      }

      // Fit map to layer bounds if it's the first active layer (including photo layers)
      if (activeLayers.length === 1 && geoJsonData.features && geoJsonData.features.length > 0) {
        const bounds = new window.mapboxgl.LngLatBounds();
        geoJsonData.features.forEach(feature => {
          if (feature.geometry.type === 'Point') {
            bounds.extend(feature.geometry.coordinates);
          } else if (feature.geometry.coordinates) {
            const coords = flattenCoordinates(feature.geometry.coordinates);
            coords.forEach(coord => bounds.extend(coord));
          }
        });
        
        if (!bounds.isEmpty()) {
          const defaultPadding = 50;
          const leftPanelWidth = isLeftPanelOpen ? 398 : 48; // Left panel OVERLAPS map
          // Right panel is separate, doesn't overlap, so no extra padding needed
          
          const padding = {
            top: defaultPadding,
            bottom: defaultPadding,
            left: leftPanelWidth + defaultPadding, // Account for overlapping left panel
            right: defaultPadding // Right panel doesn't overlap
          };
          
          map.fitBounds(bounds, { padding });
        }
      }
    } catch (error) {
      console.error('Error adding vector layer:', error);
      throw error;
    }
  };

  const removeLayerFromMap = (dataset) => {
    if (!map) return;

    const layerId = `layer-${dataset.id}`;
    const sourceId = `source-${dataset.id}`;

    // Remove all related layers
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
    if (map.getLayer(layerId + '-labels')) {
      map.removeLayer(layerId + '-labels');
    }
    if (map.getLayer(layerId + '-fill')) {
      map.removeLayer(layerId + '-fill');
    }
    if (map.getLayer(layerId + '-stroke')) {
      map.removeLayer(layerId + '-stroke');
    }
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    // Clean up photo markers if this is a photo layer
    const isPhotoLayer = dataset.geometry_type?.toLowerCase() === 'photocollection' || 
                        dataset.name?.toLowerCase().includes('photo') ||
                        dataset.file_name?.toLowerCase().includes('photo');
    if (isPhotoLayer && window.photoMarkersByLayer) {
      const layerMarkers = window.photoMarkersByLayer.get(layerId);
      if (layerMarkers) {
        layerMarkers.forEach(marker => marker.remove());
        window.photoMarkersByLayer.delete(layerId);
      }
    }

    // Update layer state
    setLayerStates(prev => {
      const next = new Map(prev);
      next.delete(dataset.id);
      return next;
    });
  };

  const toggleLayerVisibility = (dataset) => {
    if (!map) return;

    const layerId = `layer-${dataset.id}`;
    const currentState = layerStates.get(dataset.id);
    const newVisibility = !currentState?.visible;

    // Toggle visibility for all related layers
    const layerIds = [layerId, layerId + '-labels', layerId + '-fill', layerId + '-stroke'];
    layerIds.forEach(id => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', newVisibility ? 'visible' : 'none');
      }
    });

    // Update state
    setLayerStates(prev => new Map(prev.set(dataset.id, { 
      ...currentState, 
      visible: newVisibility 
    })));
  };

  const handleRemoveLayer = (dataset) => {
    removeLayerFromMap(dataset);
    
    // Remove layer from any groups it belongs to
    setLayerGroups(prev => prev.map(group => ({
      ...group,
      layers: group.layers.filter(l => l.id !== dataset.id)
    })));
    
    onRemoveLayer(dataset.id);
  };

  const handleClearAll = () => {
    activeLayers.forEach(layer => removeLayerFromMap(layer));
    onClearAllLayers();
    setLayerStates(new Map());
    
    // Clear all groups since all layers are being removed
    setLayerGroups([]);
  };

  const zoomToLayer = async (dataset) => {
    if (!map || (!isAuthenticated && !isDemoMode)) return;

    
    try {
      // Get the layer data from the API
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${dataset.id}/geojson`);
      const layerData = await response.json();

      let bounds = new window.mapboxgl.LngLatBounds();

      // Handle raster data
      if (layerData.type === 'raster' && layerData.rasterData && layerData.rasterData.bbox) {
        const bbox = layerData.rasterData.bbox;
        bounds = new window.mapboxgl.LngLatBounds([
          [bbox[0], bbox[1]], // southwest
          [bbox[2], bbox[3]]  // northeast
        ]);
      }
      // Handle vector data
      else if (layerData.features && layerData.features.length > 0) {
        
        layerData.features.forEach((feature, index) => {
          if (feature.geometry.type === 'Point') {
            bounds.extend(feature.geometry.coordinates);
          } else if (feature.geometry.coordinates) {
            const coords = flattenCoordinates(feature.geometry.coordinates);
            coords.forEach(coord => bounds.extend(coord));
          }
        });
      } else {
        return;
      }
      
      if (!bounds.isEmpty()) {
        
        // Calculate padding to account for overlapping left panel
        const defaultPadding = 50;
        const leftPanelWidth = isLeftPanelOpen ? 398 : 48; // Left panel OVERLAPS map
        // Right panel is separate, doesn't overlap, so no extra padding needed
        
        const padding = {
          top: defaultPadding,
          bottom: defaultPadding,
          left: leftPanelWidth + defaultPadding, // Account for overlapping left panel
          right: defaultPadding // Right panel doesn't overlap
        };
        
        map.fitBounds(bounds, { padding });
      } else {
      }
    } catch (err) {
      console.error('Error zooming to layer:', err);
    }
  };

  const getLayerStyle = (geometryType, layerId, sourceId, isPhotoLayer = false) => {
    const baseStyle = {
      id: layerId,
      source: sourceId
    };

    // Special styling for photo collections - return null to let MapViewer handle with custom markers
    if (isPhotoLayer || geometryType?.toLowerCase() === 'photocollection') {
      return null; // Don't add any layers - MapViewer will handle with custom HTML markers
    }

    switch (geometryType?.toLowerCase()) {
      case 'point':
      case 'multipoint':
        return [
          {
            ...baseStyle,
            type: 'circle',
            paint: {
              'circle-radius': 6,
              'circle-color': '#007cbf',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff'
            }
          },
          {
            ...baseStyle,
            id: layerId + '-labels',
            type: 'symbol',
            layout: {
              'text-field': ['get', '_label'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 12,
              'text-offset': [0, 0],
              'text-anchor': 'bottom',
              'text-optional': true
            },
            paint: {
              'text-color': '#000000',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1
            },
            filter: ['has', '_label']
          }
        ];
      
      case 'linestring':
      case 'multilinestring':
        return [
          {
            ...baseStyle,
            type: 'line',
            paint: {
              'line-color': '#007cbf',
              'line-width': 3
            }
          },
          {
            ...baseStyle,
            id: layerId + '-labels',
            type: 'symbol',
            layout: {
              'text-field': ['get', '_label'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 12,
              'text-offset': [0, 0],
              'text-anchor': 'bottom',
              'text-optional': true,
              'symbol-placement': 'line'
            },
            paint: {
              'text-color': '#000000',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1
            },
            filter: ['has', '_label']
          }
        ];
      
      case 'polygon':
      case 'multipolygon':
        return [
          {
            ...baseStyle,
            id: layerId + '-fill',
            type: 'fill',
            paint: {
              'fill-color': '#007cbf',
              'fill-opacity': 0.3
            }
          },
          {
            ...baseStyle,
            id: layerId + '-stroke',
            type: 'line',
            paint: {
              'line-color': '#007cbf',
              'line-width': 2
            }
          },
          {
            ...baseStyle,
            id: layerId + '-labels',
            type: 'symbol',
            layout: {
              'text-field': ['get', '_label'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 12,
              'text-offset': [0, 0],
              'text-anchor': 'center',
              'text-optional': true
            },
            paint: {
              'text-color': '#000000',
              'text-halo-color': '#ffffff',
              'text-halo-width': 1
            },
            filter: ['has', '_label']
          }
        ];
      
      case 'raster':
        return {
          ...baseStyle,
          type: 'raster',
          paint: {
            'raster-opacity': 0.8
          }
        };
      
      default:
        return {
          ...baseStyle,
          type: 'circle',
          paint: {
            'circle-radius': 4,
            'circle-color': '#007cbf'
          }
        };
    }
  };

  const flattenCoordinates = (coords) => {
    const result = [];
    
    const flatten = (arr) => {
      for (let i = 0; i < arr.length; i++) {
        if (Array.isArray(arr[i])) {
          if (typeof arr[i][0] === 'number' && typeof arr[i][1] === 'number') {
            // This is a coordinate pair [lng, lat]
            result.push([arr[i][0], arr[i][1]]);
          } else {
            // This is another array level, recurse
            flatten(arr[i]);
          }
        }
      }
    };
    
    flatten(coords);
    return result;
  };

  const mapStyles = [
    { id: 'mapbox://styles/mapbox/streets-v11', name: 'Streets' },
    { id: 'mapbox://styles/mapbox/satellite-v9', name: 'Satellite' },
    { id: 'mapbox://styles/mapbox/satellite-streets-v12', name: 'Satellite Streets' },
    { id: 'mapbox://styles/mapbox/light-v11', name: 'Light' },
    { id: 'mapbox://styles/mapbox/dark-v11', name: 'Dark' },
    { id: 'mapbox://styles/mapbox/outdoors-v12', name: 'Outdoors' },
    { id: 'mapbox://styles/mapbox/navigation-day-v1', name: 'Navigation Day' },
    { id: 'mapbox://styles/mapbox/navigation-night-v1', name: 'Navigation Night' }
  ];

  const handleMapStyleChange = (styleUrl) => {
    if (!map) return;
    
    // Store current map state
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    const currentBearing = map.getBearing();
    const currentPitch = map.getPitch();
    
    // Store current layers before style change
    const currentLayers = new Map();
    activeLayers.forEach(layer => {
      const layerState = layerStates.get(layer.id);
      if (layerState && layerState.loaded) {
        currentLayers.set(layer.id, layer);
      }
    });

    // Change map style
    map.setStyle(styleUrl);
    setCurrentMapStyle(styleUrl);

    // Re-add layers and restore view after style loads
    map.once('styledata', () => {
      // Ensure flat projection (not globe)
      if (map.setProjection) {
        map.setProjection('mercator');
      }
      
      // Restore map view
      map.setCenter(currentCenter);
      map.setZoom(currentZoom);
      map.setBearing(currentBearing);
      map.setPitch(currentPitch);
      
      // Re-add layers
      currentLayers.forEach(layer => {
        addLayerToMap(layer);
      });
    });
  };

  const getCurrentStyleName = () => {
    const style = mapStyles.find(s => s.id === currentMapStyle);
    return style ? style.name : 'Unknown';
  };

  const openStylePanel = (layer) => {
    setSelectedLayerForStyling(layer);
    setShowStylePanel(true);
  };

  const closeStylePanel = () => {
    setShowStylePanel(false);
    setSelectedLayerForStyling(null);
  };

  const openLabelOptionsPanel = (layer) => {
    setSelectedLayerForLabels(layer);
    setShowLabelOptionsPanel(true);
  };

  const closeLabelOptionsPanel = () => {
    setShowLabelOptionsPanel(false);
    setSelectedLayerForLabels(null);
  };

  const openLayerInfoPanel = async (layer) => {
    try {
      // Fetch complete dataset metadata from backend
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets/${layer.id}/metadata`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSelectedLayerForInfo(result.data);
        } else {
          // Fallback to basic layer info if fetch fails
          setSelectedLayerForInfo(layer);
        }
      } else {
        // Fallback to basic layer info if fetch fails
        setSelectedLayerForInfo(layer);
      }
    } catch (error) {
      console.error('Error fetching complete dataset metadata:', error);
      // Fallback to basic layer info if fetch fails
      setSelectedLayerForInfo(layer);
    }
    setShowLayerInfoPanel(true);
  };

  const closeLayerInfoPanel = () => {
    setShowLayerInfoPanel(false);
    setSelectedLayerForInfo(null);
  };

  // Context menu functions
  const handleContextMenu = (e, layer) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate menu dimensions (approximate)
    const menuWidth = 160;
    const menuHeight = 180; // Approximate height for 5 items + separators
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate initial position
    let x = e.clientX;
    let y = e.clientY;
    
    // Adjust X position if menu would extend beyond right edge
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 10; // 10px padding from edge
    }
    
    // Adjust Y position if menu would extend beyond bottom edge
    if (y + menuHeight > viewportHeight) {
      y = viewportHeight - menuHeight - 10; // 10px padding from edge
    }
    
    // Ensure menu doesn't go off the left or top edges
    x = Math.max(10, x);
    y = Math.max(10, y);
    
    setContextMenu({
      visible: true,
      x: x,
      y: y,
      layer: layer
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, layer: null });
  };

  const handleContextMenuAction = (action, layer) => {
    switch (action) {
      case 'info':
        openLayerInfoPanel(layer);
        break;
      case 'style':
        openStylePanel(layer);
        break;
      case 'label':
        openLabelOptionsPanel(layer);
        break;
      case 'zoom':
        zoomToLayer(layer);
        break;
      case 'visibility':
        toggleLayerVisibility(layer);
        break;
      case 'delete':
        handleRemoveLayer(layer);
        break;
      default:
        break;
    }
    closeContextMenu();
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible && contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        closeContextMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.visible]);

  // Group management functions
  const createGroup = (parentGroupId = null) => {
    // Handle case where event object is passed instead of parentGroupId
    const actualParentId = (parentGroupId && typeof parentGroupId === 'string') ? parentGroupId : null;
    
    const newGroup = {
      id: Date.now().toString(),
      name: '',
      layers: [],
      subgroups: [],
      visible: true,
      expanded: true,
      parentId: actualParentId
    };
    
    if (actualParentId) {
      // Add as subgroup
      setLayerGroups(prev => prev.map(group => 
        group.id === actualParentId 
          ? { ...group, subgroups: [...group.subgroups, newGroup] }
          : group
      ));
    } else {
      // Add as top-level group
      setLayerGroups(prev => [...prev, newGroup]);
    }
    
    // Immediately enter edit mode for the new group with empty name
    setEditingGroupId(newGroup.id);
    setEditingGroupName('');
  };

  const deleteGroup = (groupId) => {
    setLayerGroups(prev => prev.filter(group => group.id !== groupId));
  };

  const startEditingGroup = (groupId, groupName) => {
    setEditingGroupId(groupId);
    setEditingGroupName(groupName);
  };

  const saveGroupName = (groupId) => {
    const finalName = editingGroupName.trim() || 'New Group';
    
    setLayerGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, name: finalName }
        : group
    ));
    
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  const cancelEditingGroup = () => {
    // If canceling a new group with no name, give it a default name
    if (editingGroupId) {
      const group = layerGroups.find(g => g.id === editingGroupId);
      if (group && !group.name) {
        setLayerGroups(prev => prev.map(g => 
          g.id === editingGroupId 
            ? { ...g, name: 'New Group' }
            : g
        ));
      }
    }
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  const handleGroupNameKeyPress = (e, groupId) => {
    if (e.key === 'Enter') {
      saveGroupName(groupId);
    } else if (e.key === 'Escape') {
      cancelEditingGroup();
    }
  };

  const toggleGroupExpanded = (groupId) => {
    setLayerGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, expanded: !group.expanded }
        : group
    ));
  };

  const toggleGroupVisibility = (groupId) => {
    setLayerGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        const newVisible = !group.visible;
        // Toggle visibility of all layers in the group
        group.layers.forEach(layer => {
          const layerIds = [`layer-${layer.id}`, `layer-${layer.id}-fill`, `layer-${layer.id}-stroke`];
          layerIds.forEach(id => {
            if (map && map.getLayer(id)) {
              map.setLayoutProperty(id, 'visibility', newVisible ? 'visible' : 'none');
            }
          });
          setLayerStates(prevStates => new Map(prevStates.set(layer.id, {
            ...prevStates.get(layer.id),
            visible: newVisible
          })));
        });
        return { ...group, visible: newVisible };
      }
      return group;
    }));
  };




  const handleDragLeave = (e) => {
    // Only clear drag over if we're actually leaving the group area
    // This prevents flickering when moving over child elements
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverPosition(null);
    }
  };




  // Helper function to get ungrouped layers
  const getUngroupedLayers = () => {
    const groupedLayerIds = new Set();
    
    // Recursively collect layer IDs from all groups and subgroups
    const collectLayerIds = (groups) => {
      groups.forEach(group => {
        group.layers.forEach(layer => groupedLayerIds.add(layer.id));
        if (group.subgroups && group.subgroups.length > 0) {
          collectLayerIds(group.subgroups);
        }
      });
    };
    
    collectLayerIds(layerGroups);
    return activeLayers.filter(layer => !groupedLayerIds.has(layer.id));
  };

  // Unified drag and drop handlers
  const handleDragStart = (e, item, itemType) => {
    e.stopPropagation(); // Prevent event bubbling to parent groups
    const dragData = { ...item, type: itemType };
    setDraggedItem(dragData);
    if (itemType === 'group') {
      setDraggedGroup(item);
    }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetId, targetType, position = 'into') => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem) return;
    
    // Prevent dropping item on itself
    if (draggedItem.id === targetId) return;
    
    // Prevent dropping group into its own subgroups
    if (draggedItem.type === 'group' && targetType === 'group') {
      const isDescendant = checkIfDescendant(draggedItem.id, targetId);
      if (isDescendant) return;
    }
    
    e.dataTransfer.dropEffect = 'move';
    setDragOverPosition({ 
      targetId, 
      targetType,
      position
    });
  };

  // Helper to check if target is descendant of source group
  const checkIfDescendant = (sourceGroupId, targetGroupId) => {
    const findInGroups = (groups, groupId) => {
      for (const group of groups) {
        if (group.id === groupId) return group;
        if (group.subgroups) {
          const found = findInGroups(group.subgroups, groupId);
          if (found) return found;
        }
      }
      return null;
    };
    
    const sourceGroup = findInGroups(layerGroups, sourceGroupId);
    if (!sourceGroup) return false;
    
    const checkDescendants = (group) => {
      if (group.id === targetGroupId) return true;
      if (group.subgroups) {
        return group.subgroups.some(checkDescendants);
      }
      return false;
    };
    
    return checkDescendants(sourceGroup);
  };

  const handleDrop = (e, targetId, targetType, position = 'into') => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem || draggedItem.id === targetId) return;
    
    if (draggedItem.type === 'layer') {
      handleLayerDrop(targetId, targetType, position);
    } else if (draggedItem.type === 'group') {
      handleGroupDrop(targetId, targetType, position);
    }
    
    // Clean up drag state
    setDraggedItem(null);
    setDraggedGroup(null);
    setDragOverPosition(null);
  };

  const handleLayerDrop = (targetId, targetType, position) => {
    if (targetType === 'group' && position === 'into') {
      // Add layer to group
      setLayerGroups(prev => prev.map(group => 
        group.id === targetId 
          ? { ...group, layers: [...group.layers, draggedItem] }
          : { ...group, layers: group.layers.filter(l => l.id !== draggedItem.id) }
      ));
    } else if (targetType === 'layer' && position === 'reorder') {
      // Remove layer from any group first
      setLayerGroups(prev => prev.map(group => ({
        ...group,
        layers: group.layers.filter(l => l.id !== draggedItem.id)
      })));
      
      // Handle layer reordering at top level
      if (onReorderLayers) {
        const ungroupedLayers = getUngroupedLayers();
        const targetIndex = ungroupedLayers.findIndex(layer => layer.id === targetId);
        if (targetIndex !== -1) {
          const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
          onReorderLayers(draggedItem.id, insertIndex);
        }
      }
    } else {
      // Remove layer from any group (move to top level)
      setLayerGroups(prev => prev.map(group => ({
        ...group,
        layers: group.layers.filter(l => l.id !== draggedItem.id)
      })));
    }
  };

  const handleGroupDrop = (targetId, targetType, position) => {
    setLayerGroups(prev => {
      // Enhanced recursive function to remove group from anywhere in tree
      const removeGroupFromTree = (groups) => {
        return groups.reduce((acc, group) => {
          if (group.id === draggedItem.id) {
            return acc; // Remove this group
          }
          
          // Process subgroups recursively
          const updatedSubgroups = group.subgroups ? removeGroupFromTree(group.subgroups) : [];
          
          return [...acc, {
            ...group,
            subgroups: updatedSubgroups
          }];
        }, []);
      };
      
      // Enhanced recursive function to add group as subgroup anywhere in tree
      const addGroupAsSubgroup = (groups, targetGroupId, groupToAdd) => {
        return groups.map(group => {
          if (group.id === targetGroupId) {
            return {
              ...group,
              subgroups: [...(group.subgroups || []), { ...groupToAdd, parentId: targetGroupId }]
            };
          }
          
          // Check subgroups recursively
          if (group.subgroups && group.subgroups.length > 0) {
            return {
              ...group,
              subgroups: addGroupAsSubgroup(group.subgroups, targetGroupId, groupToAdd)
            };
          }
          
          return group;
        });
      };
      
      let groups = removeGroupFromTree(prev);
      
      if (targetType === 'group' && position === 'into') {
        // Add as subgroup (could be nested anywhere)
        // Create a clean copy of the dragged group with new parentId
        const cleanDraggedGroup = {
          id: draggedItem.id,
          name: draggedItem.name,
          layers: draggedItem.layers || [],
          subgroups: draggedItem.subgroups || [],
          visible: draggedItem.visible !== undefined ? draggedItem.visible : true,
          expanded: draggedItem.expanded !== undefined ? draggedItem.expanded : true,
          parentId: targetId
        };
        groups = addGroupAsSubgroup(groups, targetId, cleanDraggedGroup);
      } else if (targetType === 'toplevel' || !targetId) {
        // Add to top level (dropped on main panel)
        const draggedGroupWithoutParent = {
          id: draggedItem.id,
          name: draggedItem.name,
          layers: draggedItem.layers || [],
          subgroups: draggedItem.subgroups || [],
          visible: draggedItem.visible !== undefined ? draggedItem.visible : true,
          expanded: draggedItem.expanded !== undefined ? draggedItem.expanded : true,
          parentId: null
        };
        groups.push(draggedGroupWithoutParent);
      } else {
        // Add to top level with positioning
        const draggedGroupWithoutParent = {
          id: draggedItem.id,
          name: draggedItem.name,
          layers: draggedItem.layers || [],
          subgroups: draggedItem.subgroups || [],
          visible: draggedItem.visible !== undefined ? draggedItem.visible : true,
          expanded: draggedItem.expanded !== undefined ? draggedItem.expanded : true,
          parentId: null
        };
        if (targetType === 'group') {
          const targetIndex = groups.findIndex(g => g.id === targetId);
          if (targetIndex !== -1) {
            const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
            groups.splice(insertIndex, 0, draggedGroupWithoutParent);
          } else {
            groups.push(draggedGroupWithoutParent);
          }
        } else {
          groups.push(draggedGroupWithoutParent);
        }
      }
      
      return groups;
    });
  };

  // Recursive component to render groups and subgroups
  const renderGroup = (group, depth = 0) => {
    const isBeingDraggedOver = dragOverPosition && dragOverPosition.targetId === group.id && dragOverPosition.targetType === 'group';
    const dragOverClass = isBeingDraggedOver ? `drag-over-${dragOverPosition.position}` : '';
    
    return (
      <div 
        key={group.id} 
        className={`layer-group ${dragOverClass}`}
        style={{ marginLeft: `${depth * 20}px` }}
        draggable={!editingGroupId}
        onDragStart={(e) => handleDragStart(e, group, 'group')}
        onDragOver={(e) => handleDragOver(e, group.id, 'group', 'into')}
        onDrop={(e) => handleDrop(e, group.id, 'group', 'into')}
      >
        {/* Drop zones for before and after group positioning */}
        <div 
          className="group-drop-zone group-drop-before"
          onDragOver={(e) => handleDragOver(e, group.id, 'group', 'before')}
          onDrop={(e) => handleDrop(e, group.id, 'group', 'before')}
        />
        
        <div 
          className={`group-header ${isBeingDraggedOver && dragOverPosition.position === 'into' ? 'drag-over' : ''}`}
          onDragOver={(e) => handleDragOver(e, group.id, 'group', 'into')}
          onDrop={(e) => handleDrop(e, group.id, 'group', 'into')}
        >
          <button
            onClick={() => toggleGroupExpanded(group.id)}
            className={`group-toggle ${group.expanded ? 'expanded' : ''}`}
          >
            ▶
          </button>
          
          {editingGroupId === group.id ? (
            <input
              type="text"
              value={editingGroupName}
              onChange={(e) => setEditingGroupName(e.target.value)}
              onBlur={() => saveGroupName(group.id)}
              onKeyDown={(e) => handleGroupNameKeyPress(e, group.id)}
              className="group-name-input"
              placeholder="Enter group name"
              autoFocus
            />
          ) : (
            <span 
              className={`group-name ${!group.name ? 'empty' : ''}`}
              onClick={() => startEditingGroup(group.id, group.name)}
              title="Click to rename"
            >
              {group.name || 'Enter group name'}
            </span>
          )}
          
          <div className="group-controls-buttons">
            <button
              onClick={() => deleteGroup(group.id)}
              className="btn-delete-group"
              title="Delete group"
            >
              ✕
            </button>
          </div>
        </div>

        {group.expanded && (
          <div 
            className="group-layers"
            onDragOver={(e) => handleDragOver(e, group.id, 'group', 'into')}
            onDrop={(e) => handleDrop(e, group.id, 'group', 'into')}
          >
            {/* Render subgroups first */}
            {group.subgroups && group.subgroups.map(subgroup => 
              renderGroup(subgroup, depth + 1)
            )}
            
            {/* Then render layers */}
            {group.layers.length === 0 && (!group.subgroups || group.subgroups.length === 0) ? (
              <div 
                className="empty-group"
                onDragOver={(e) => handleDragOver(e, group.id, 'group', 'into')}
                onDrop={(e) => handleDrop(e, group.id, 'group', 'into')}
              >
                Drop layers here
              </div>
            ) : (
              group.layers.map((layer, layerIndex) => {
                const layerState = layerStates.get(layer.id);
                const isVisible = layerState?.visible !== false;
                const isDragOver = dragOverPosition && 
                                 dragOverPosition.targetId === layer.id && 
                                 dragOverPosition.targetType === 'layer';
                const isPhotoLayer = layer.geometry_type?.toLowerCase() === 'photocollection' || 
                                   layer.name?.toLowerCase().includes('photo') ||
                                   layer.file_name?.toLowerCase().includes('photo');
                
                return (
                  <div 
                    key={layer.id} 
                    className={`active-layer-item grouped ${isVisible ? 'visible' : 'hidden'} ${isDragOver ? `drop-${dragOverPosition.position}` : ''} ${isDemoMode && layer.id?.includes('demo') ? 'demo-layer' : ''} ${isPhotoLayer ? 'photo-layer' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, layer, 'layer')}
                    onDragOver={(e) => {
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const y = e.clientY - rect.top;
                      const height = rect.height;
                      const position = y < height / 2 ? 'before' : 'after';
                      handleDragOver(e, layer.id, 'layer', position);
                    }}
                    onDrop={(e) => handleDrop(e, layer.id, 'layer', 'reorder')}
                  >
                    <div className="layer-info">
                      <LayerSymbologyIcon 
                        layer={layer} 
                        map={map} 
                        layerStates={layerStates} 
                        onOpenStylePanel={openStylePanel}
                        isStylePanelOpen={showStylePanel && selectedLayerForStyling?.id === layer.id}
                      />
                      <div className="layer-name" title={layer.name || layer.file_name}>
                        {layer.name || layer.file_name || 'NO NAME'}
                      </div>
                    </div>
                    
                    <div className="layer-controls">
                      <button
                        onClick={() => {/* TODO: Implement share data functionality */}}
                        className="btn-share-data"
                        title="Share this layer data"
                      >
                        🔗 Share
                      </button>
                      
                      <button
                        onClick={(e) => handleContextMenu(e, layer)}
                        className="btn-context-menu btn-small"
                        title="Layer options"
                      >
                        ⋯
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
        
        <div 
          className="group-drop-zone group-drop-after"
          onDragOver={(e) => handleDragOver(e, group.id, 'group', 'after')}
          onDrop={(e) => handleDrop(e, group.id, 'group', 'after')}
        />
      </div>
    );
  };

  if (loading && activeLayers.length === 0) {
    return (
      <div className="active-layers-panel">
        <div className="loading">Loading layers...</div>
      </div>
    );
  }

  return (
    <div className="active-layers-panel">
      {!hideShareControls && (
        <div className="map-controls">
          <button 
            onClick={() => setShowSharingDialog(true)} 
            className="btn-share-map"
            title="Share this map"
            disabled={!map || activeLayers.length === 0}
          >
            🔗 Share Map
          </button>
        </div>
      )}

      {/* Add Group Button */}
      <div className="group-controls">
        <button onClick={() => createGroup()} className="btn-create-group">
          ➕ Add Group
        </button>
      </div>

      <div 
        ref={listRef}
        className="active-layers-list"
        onDragOver={(e) => {
          // Only handle if not over a specific drop target (group header, layer item, etc.)
          // But allow drops in empty areas of the main panel
          const isOverGroupHeader = e.target.closest('.group-header');
          const isOverLayerItem = e.target.closest('.active-layer-item');
          const isOverGroupDropZone = e.target.closest('.group-drop-zone');
          const isOverEmptyGroup = e.target.closest('.empty-group');
          
          if (!isOverGroupHeader && !isOverLayerItem && !isOverGroupDropZone && !isOverEmptyGroup) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverPosition(null);
          }
        }}
        onDrop={(e) => {
          // Only handle if not over a specific drop target (group header, layer item, etc.)
          // But allow drops in empty areas of the main panel
          const isOverGroupHeader = e.target.closest('.group-header');
          const isOverLayerItem = e.target.closest('.active-layer-item');
          const isOverGroupDropZone = e.target.closest('.group-drop-zone');
          const isOverEmptyGroup = e.target.closest('.empty-group');
          
          // If we're over the main panel background (not specific interactive elements)
          if (!isOverGroupHeader && !isOverLayerItem && !isOverGroupDropZone && !isOverEmptyGroup) {
            e.preventDefault();
            e.stopPropagation(); // Prevent parent handlers from interfering
            
            if (draggedItem) {
              if (draggedItem.type === 'layer') {
                // Remove layer from any group (move to top level)
                setLayerGroups(prev => prev.map(group => ({
                  ...group,
                  layers: group.layers.filter(l => l.id !== draggedItem.id)
                })));
              } else if (draggedItem.type === 'group') {
                // Move group to top level
                setLayerGroups(prev => {
                  // Remove group from current position (anywhere in tree)
                  const removeGroupFromTree = (groups) => {
                    return groups.reduce((acc, group) => {
                      if (group.id === draggedItem.id) {
                        return acc; // Remove this group from current level
                      }
                      
                      // Process subgroups recursively - check if we need to remove any subgroups
                      let updatedSubgroups = [];
                      if (group.subgroups) {
                        updatedSubgroups = removeGroupFromTree(group.subgroups);
                      }
                      
                      return [...acc, {
                        ...group,
                        subgroups: updatedSubgroups
                      }];
                    }, []);
                  };
                  
                  let groups = removeGroupFromTree(prev);
                  
                  // Add to top level
                  const draggedGroupWithoutParent = {
                    id: draggedItem.id,
                    name: draggedItem.name,
                    layers: draggedItem.layers || [],
                    subgroups: draggedItem.subgroups || [],
                    visible: draggedItem.visible !== undefined ? draggedItem.visible : true,
                    expanded: draggedItem.expanded !== undefined ? draggedItem.expanded : true,
                    parentId: null
                  };
                  groups.push(draggedGroupWithoutParent);
                  
                  return groups;
                });
              }
              setDraggedItem(null);
              setDraggedGroup(null);
              setDragOverPosition(null);
            }
          }
        }}
      >
        <>
          {/* Drop line indicator */}
          <div className="drop-line-indicator" style={dropLineStyle} />

          {/* Render Groups */}
          {layerGroups.filter(group => !group.parentId).map(group => renderGroup(group))}
            {/* Render Ungrouped Layers */}
            {getUngroupedLayers().map((layer, layerIndex) => {
              const layerState = layerStates.get(layer.id);
              const isVisible = layerState?.visible !== false;
              const isDragOver = dragOverPosition && 
                               dragOverPosition.targetId === layer.id && 
                               dragOverPosition.targetType === 'layer';
              const isPhotoLayer = layer.geometry_type?.toLowerCase() === 'photocollection' || 
                                 layer.name?.toLowerCase().includes('photo') ||
                                 layer.file_name?.toLowerCase().includes('photo');
              
              return (
                <div 
                  key={layer.id} 
                  className={`active-layer-item ${isVisible ? 'visible' : 'hidden'} ${isDragOver ? `drop-${dragOverPosition.position}` : ''} ${isDemoMode && layer.id?.includes('demo') ? 'demo-layer' : ''} ${isPhotoLayer ? 'photo-layer' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, layer, 'layer')}
                  onDragOver={(e) => {
                    e.preventDefault();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const height = rect.height;
                    const position = y < height / 2 ? 'before' : 'after';
                    handleDragOver(e, layer.id, 'layer', position);
                  }}
                  onDrop={(e) => handleDrop(e, layer.id, 'layer', 'reorder')}
                >
                  <div className="layer-info">
                    <LayerSymbologyIcon 
                      layer={layer} 
                      map={map} 
                      layerStates={layerStates} 
                      onOpenStylePanel={openStylePanel}
                      isStylePanelOpen={showStylePanel && selectedLayerForStyling?.id === layer.id}
                    />
                    <div className="layer-name" title={layer.name || layer.file_name}>
                      {layer.name || layer.file_name || 'NO NAME'}
                    </div>
                  </div>
                  
                  <div className="layer-controls">
                    <button
                      onClick={() => {/* TODO: Implement share data functionality */}}
                      className="btn-share-data"
                      title="Share this layer data"
                    >
                      🔗 Share
                    </button>
                    
                    <button
                      onClick={(e) => handleContextMenu(e, layer)}
                      className="btn-context-menu btn-small"
                      title="Layer options"
                    >
                      ⋯
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Show message when no layers exist */}
            {activeLayers.length === 0 && layerGroups.length === 0 && (
              <div className="no-layers">
                No active layers. Add layers from the Data Sources panel to visualize them on the map.
              </div>
            )}
          </>
      </div>

      <div className="map-style-selector">
        <div className="map-style-header">
          <span className="map-style-label">Background Map:</span>
          <select 
            value={currentMapStyle} 
            onChange={(e) => handleMapStyleChange(e.target.value)}
            className="map-style-dropdown"
            disabled={!map}
          >
            {mapStyles.map(style => (
              <option key={style.id} value={style.id}>
                {style.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Layer Style Panel */}
      <LayerStylePanel
        map={map}
        layer={selectedLayerForStyling}
        isOpen={showStylePanel}
        onClose={closeStylePanel}
      />

      {/* Text Labels Panel */}
      <LabelOptionsPanel
        map={map}
        layer={selectedLayerForLabels}
        isOpen={showLabelOptionsPanel}
        onClose={closeLabelOptionsPanel}
      />

      {/* Layer Info Panel */}
      <LayerInfoPanel
        layer={selectedLayerForInfo}
        isOpen={showLayerInfoPanel}
        onClose={closeLayerInfoPanel}
      />

      {!hideShareControls && (
        <MapSharingDialog
          isOpen={showSharingDialog}
          onClose={() => {
            setShowSharingDialog(false);
          }}
          map={map}
          activeLayers={activeLayers}
          layerGroups={layerGroups}
          isLeftPanelOpen={isLeftPanelOpen}
        />
      )}

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="layer-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 1000
          }}
        >
          <div className="context-menu-item" onClick={() => handleContextMenuAction('style', contextMenu.layer)}>
            <span className="context-menu-icon">🎨</span>
            Style Layer
          </div>
          {contextMenu.layer?.geometry_type !== 'Raster' && (
            <div className="context-menu-item" onClick={() => handleContextMenuAction('label', contextMenu.layer)}>
              <span className="context-menu-icon">🏷️</span>
              Text Labels
            </div>
          )}
          <div className="context-menu-item" onClick={() => handleContextMenuAction('zoom', contextMenu.layer)}>
            <span className="context-menu-icon">🎯</span>
            Zoom to Layer
          </div>
          <div className="context-menu-item" onClick={() => handleContextMenuAction('info', contextMenu.layer)}>
            <span className="context-menu-icon">ℹ️</span>
            Layer Info
          </div>
          <div className="context-menu-separator"></div>
          <div className="context-menu-item" onClick={() => handleContextMenuAction('visibility', contextMenu.layer)}>
            <span className="context-menu-icon">{(() => {
              const layerState = layerStates.get(contextMenu.layer.id);
              const isVisible = layerState?.visible !== false;
              return isVisible ? '👁️' : '🙈';
            })()}</span>
            {(() => {
              const layerState = layerStates.get(contextMenu.layer.id);
              const isVisible = layerState?.visible !== false;
              return isVisible ? 'Hide Layer' : 'Show Layer';
            })()}
          </div>
          <div className="context-menu-item danger" onClick={() => handleContextMenuAction('delete', contextMenu.layer)}>
            <span className="context-menu-icon">✕</span>
            Remove from Map
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveLayersPanel;
